import { useState } from 'react';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { UserAvatar } from '@/components/UserAvatar';
import { usePipelineBoard } from '@/state/pipelines/usePipelineBoard';
import { ArrowLeft, Pause, Play, Plus, ShieldAlert, SkipForward } from 'lucide-react';
import { cn } from 'helpers/cn';
import { FOCUS_RING } from '../../ConsolePanel';
import { StageColumn } from './StageColumn';
import { DraftTray } from './DraftTray';
import { AutomationPanel } from './AutomationPanel';
import { PIPELINE_STATUS_TONES } from './labels';

type PipelineBoardProps = {
  pipelineId: number;
  onBack: () => void;
};

/**
 * Secondary control: a hairline button on the panel, never a filled one.
 *
 * `border-input` rather than `border-line`: it carries no fill, so the outline is the
 * only thing drawing it, and 10% alpha is not an outline. `h-9` below `md` clears the
 * 36px touch floor; the desktop height is unchanged.
 */
const SECONDARY_BUTTON = cn(
  'border-input text-dim hover:text-foreground hover:bg-hover flex h-9 shrink-0 items-center gap-1.5 rounded-lg',
  'border bg-transparent px-2.5 text-meta transition-colors duration-150 disabled:opacity-40 md:h-7',
  FOCUS_RING,
);

export const PipelineBoard = ({ pipelineId, onBack }: PipelineBoardProps) => {
  const manager = usePipelineBoard(pipelineId);
  const { pipeline, isLoading, stages, experimentsById, available, isDirty, isSaving, hasBlocking } = manager;

  /**
   * Both of these stop live experiments and neither can be undone — Cancelled is a terminal
   * status, and an ended experiment does not restart. One piece of state rather than two
   * booleans, since only one dialog can be open.
   */
  const [confirming, setConfirming] = useState<'advance' | 'cancel' | null>(null);

  if (isLoading) return <p className="supporting">Loading pipeline…</p>;
  if (!pipeline) return <p className="supporting">This pipeline no longer exists.</p>;

  // The product tour points at one experiment card: the first in the first stage that has any.
  const tourCardStage = stages.findIndex((stage) => stage.experimentIds.some((id) => experimentsById.has(id)));

  return (
    <div className="flex flex-col gap-[14px]">
      <section className="bg-panel border-line rounded-panel flex flex-wrap items-center gap-2 border px-3 py-2.5">
        <button
          type="button"
          onClick={onBack}
          className={cn(
            'text-dim hover:text-foreground hover:bg-hover flex h-9 shrink-0 items-center gap-1.5 rounded-lg px-2',
            'text-meta transition-colors duration-150 md:h-7',
            FOCUS_RING,
          )}
        >
          <ArrowLeft className="size-3.5" strokeWidth={1.5} />
          All pipelines
        </button>

        {/* min-w-0, or `truncate` has nothing to truncate against inside a flex row. */}
        <h2 className="min-w-0 truncate text-lede font-semibold tracking-tight" title={pipeline.name}>
          {pipeline.name}
        </h2>

        {/* Who made it, beside the name; the full name on hover. */}
        {!!pipeline.createdByUser && <UserAvatar user={pipeline.createdByUser} size="sm" tooltip />}

        <span
          className={cn('micro-label rounded-pill border px-[11px] py-[5px]', PIPELINE_STATUS_TONES[pipeline.status])}
        >
          {pipeline.status}
        </span>
        {pipeline.automationEnabled ? (
          <span className="micro-label border-ok-line text-ok rounded-pill border px-[11px] py-[5px]">
            Automation on
          </span>
        ) : (
          <span className="micro-label border-line rounded-pill border px-[11px] py-[5px]">Dry run</span>
        )}

        {/* Its own wrapping row on a phone: four verbs plus a status word do not fit on
            one 375px line beside the pipeline's name, and `ml-auto` alone would only
            push them off the right edge. */}
        <div
          className="flex w-full flex-wrap items-center gap-2 md:ml-auto md:w-auto md:flex-nowrap"
          data-tour="pipelines-controls"
        >
          {isDirty && <span className="micro-label text-warn">Unsaved changes</span>}

          {/* Only offered while a stage is actually running: it ends that stage, and with
              nothing running there is nothing for it to end. */}
          {manager.runningStageIndex !== -1 && (
            <button type="button" onClick={() => setConfirming('advance')} className={SECONDARY_BUTTON}>
              <SkipForward className="size-3.5" strokeWidth={1.5} />
              End stage {manager.runningStageIndex + 1}
            </button>
          )}

          {pipeline.status === 'Running' && (
            <button type="button" onClick={() => manager.setStatus('Paused')} className={SECONDARY_BUTTON}>
              <Pause className="size-3.5" strokeWidth={1.5} />
              Pause
            </button>
          )}
          {pipeline.status === 'Paused' && (
            <button type="button" onClick={() => manager.setStatus('Running')} className={SECONDARY_BUTTON}>
              <Play className="size-3.5" strokeWidth={1.5} />
              Resume
            </button>
          )}
          {(pipeline.status === 'Draft' || pipeline.status === 'Running' || pipeline.status === 'Paused') && (
            <button
              type="button"
              onClick={() => setConfirming('cancel')}
              className={cn(
                'bg-danger-bg border-danger-line text-danger hover:border-danger flex h-9 shrink-0 items-center',
                'rounded-lg border px-2.5 text-meta transition-colors duration-150 md:h-7',
                FOCUS_RING,
              )}
            >
              Cancel
            </button>
          )}

          <button
            type="button"
            disabled={!isDirty || isSaving}
            onClick={manager.saveBoard}
            className={cn(
              'bg-cta hover:bg-ctahover text-cta-foreground flex h-9 shrink-0 items-center rounded-lg px-3',
              'text-meta font-medium transition-colors duration-150 disabled:opacity-40 md:h-7',
              FOCUS_RING,
            )}
          >
            {isSaving ? 'Saving…' : 'Save board'}
          </button>
        </div>
      </section>

      {hasBlocking && (
        <div
          className="bg-danger-bg border-danger-line rounded-panel flex items-start gap-2 border p-3"
          data-tour="pipelines-collision"
        >
          <ShieldAlert className="text-danger mt-0.5 size-4 shrink-0" strokeWidth={1.5} />
          <div className="space-y-1">
            <p className="text-danger text-body font-medium">
              This board has a stage the engine would refuse to start.
            </p>
            <p className="supporting">
              Move one of the flagged experiments to a later stage. Running them apart costs nothing in sample size —
              every eligible visitor enters every eligible experiment.
            </p>
          </div>
        </div>
      )}

      <AutomationPanel
        key={pipeline.id}
        pipeline={pipeline}
        hasBlocking={hasBlocking}
        isDirty={isDirty}
        onSave={manager.saveSettings}
      />

      <div className="flex gap-2.5 overflow-x-auto pb-3">
        <DraftTray experiments={available} onDropExperiment={manager.removeFromBoard} />

        {stages.map((stage, index) => (
          <StageColumn
            key={stage.id ?? `new-${index}`}
            stage={stage}
            index={index}
            isFirst={index === 0}
            isLast={index === stages.length - 1}
            anchorFirstCard={index === tourCardStage}
            experiments={stage.experimentIds.flatMap((id) => {
              const experiment = experimentsById.get(id);
              return experiment ? [experiment] : [];
            })}
            collisions={manager.collisionFor(index)}
            onRename={(name) => manager.renameStage(index, name)}
            onAdvanceRuleChange={(rule) => manager.setAdvanceRule(index, rule)}
            onMove={(direction) => manager.moveStage(index, direction)}
            onDelete={() => manager.deleteStage(index)}
            onDropExperiment={(experimentId) => manager.moveToStage(experimentId, index)}
            onRemoveExperiment={manager.removeFromBoard}
          />
        ))}

        <button
          type="button"
          onClick={manager.addStage}
          data-tour="pipelines-add-stage"
          // border-input, not border-line: the dashed outline is the only thing drawing
          // this control, and at 10% alpha it is not there in either theme.
          className={cn(
            'border-input text-dim hover:text-foreground hover:bg-hover rounded-panel flex h-32 w-[270px] shrink-0',
            'flex-col items-center justify-center gap-2 border border-dashed text-meta transition-colors duration-150',
            FOCUS_RING,
          )}
        >
          <Plus className="size-4" strokeWidth={1.5} />
          Add a stage
        </button>
      </div>

      <ConfirmDialog
        open={confirming === 'advance'}
        title={`End stage ${manager.runningStageIndex + 1} now?`}
        description="Every experiment still running in this stage is stopped, and the engine arms the next stage on its next pass. Stopping an experiment cannot be undone."
        confirmLabel="End the stage"
        confirmVariant="destructive"
        onConfirm={async () => {
          await manager.advanceStage();
          setConfirming(null);
        }}
        onCancel={() => setConfirming(null)}
      />

      <ConfirmDialog
        open={confirming === 'cancel'}
        title={`Cancel "${pipeline.name}"?`}
        description="Cancelled is final — the pipeline cannot be resumed or re-run, and automation is turned off. Experiments already running are left alone and finish on their own schedules."
        confirmLabel="Cancel the pipeline"
        confirmVariant="destructive"
        onConfirm={async () => {
          await manager.setStatus('Cancelled');
          setConfirming(null);
        }}
        onCancel={() => setConfirming(null)}
      />
    </div>
  );
};
